import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { X } from '@phosphor-icons/react';

/**
 * Simple body diagram — front & back silhouette with clickable regions.
 * value: [{ body_part, description }]
 * onChange: (list) => void
 * readOnly: when true, just displays markers without allowing edits.
 */

// Region definitions: name + circle on front(F) or back(B) view (svg viewBox 100x260)
const REGIONS = [
  { part: 'Head',             view: 'F', cx: 50,  cy: 20 },
  { part: 'Face',             view: 'F', cx: 50,  cy: 30 },
  { part: 'Neck',             view: 'F', cx: 50,  cy: 42 },
  { part: 'Left Shoulder',    view: 'F', cx: 34,  cy: 55 },
  { part: 'Right Shoulder',   view: 'F', cx: 66,  cy: 55 },
  { part: 'Chest',            view: 'F', cx: 50,  cy: 72 },
  { part: 'Abdomen',          view: 'F', cx: 50,  cy: 95 },
  { part: 'Left Arm',         view: 'F', cx: 26,  cy: 85 },
  { part: 'Right Arm',        view: 'F', cx: 74,  cy: 85 },
  { part: 'Left Elbow',       view: 'F', cx: 22,  cy: 105 },
  { part: 'Right Elbow',      view: 'F', cx: 78,  cy: 105 },
  { part: 'Left Forearm',     view: 'F', cx: 20,  cy: 125 },
  { part: 'Right Forearm',    view: 'F', cx: 80,  cy: 125 },
  { part: 'Left Hand',        view: 'F', cx: 17,  cy: 148 },
  { part: 'Right Hand',       view: 'F', cx: 83,  cy: 148 },
  { part: 'Left Hip',         view: 'F', cx: 40,  cy: 130 },
  { part: 'Right Hip',        view: 'F', cx: 60,  cy: 130 },
  { part: 'Left Thigh',       view: 'F', cx: 40,  cy: 160 },
  { part: 'Right Thigh',      view: 'F', cx: 60,  cy: 160 },
  { part: 'Left Knee',        view: 'F', cx: 40,  cy: 185 },
  { part: 'Right Knee',       view: 'F', cx: 60,  cy: 185 },
  { part: 'Left Shin',        view: 'F', cx: 40,  cy: 210 },
  { part: 'Right Shin',       view: 'F', cx: 60,  cy: 210 },
  { part: 'Left Foot',        view: 'F', cx: 40,  cy: 240 },
  { part: 'Right Foot',       view: 'F', cx: 60,  cy: 240 },
  // Back view
  { part: 'Back of Head',     view: 'B', cx: 50,  cy: 20 },
  { part: 'Upper Back',       view: 'B', cx: 50,  cy: 72 },
  { part: 'Lower Back',       view: 'B', cx: 50,  cy: 100 },
  { part: 'Buttocks',         view: 'B', cx: 50,  cy: 130 },
  { part: 'Left Calf',        view: 'B', cx: 40,  cy: 210 },
  { part: 'Right Calf',       view: 'B', cx: 60,  cy: 210 },
  { part: 'Left Heel',        view: 'B', cx: 40,  cy: 245 },
  { part: 'Right Heel',       view: 'B', cx: 60,  cy: 245 },
];

const SILHOUETTE_PATH =
  // Head + neck
  "M50 5 " +
  "C60 5 62 20 60 25 " +
  "C64 30 62 38 55 40 " +
  "L55 45 " +
  // Shoulders
  "L75 55 L82 90 L78 130 " +
  // Right arm down
  "L86 150 L83 155 L78 130 " +
  // Body right side
  "L70 145 L68 175 L64 220 L62 252 L54 252 L54 175 L50 175 " +
  // Left side mirror
  "L50 175 L46 175 L46 252 L38 252 L36 220 L32 175 L30 145 " +
  // Left arm
  "L22 130 L17 155 L14 150 " +
  "L18 130 L22 90 L25 55 " +
  "L45 45 " +
  "L45 40 " +
  "C38 38 36 30 40 25 " +
  "C38 20 40 5 50 5 Z";

const BodyDiagram = ({ value = [], onChange, readOnly = false }) => {
  const [view, setView] = useState('F'); // F = front, B = back
  const [selectedIdx, setSelectedIdx] = useState(null);

  const visibleRegions = REGIONS.filter(r => r.view === view);

  const hasInjury = (part) => value.some(v => v.body_part === part);
  const findInjuryIdx = (part) => value.findIndex(v => v.body_part === part);

  const toggleRegion = (part) => {
    if (readOnly) return;
    const idx = findInjuryIdx(part);
    if (idx >= 0) {
      // Remove
      onChange(value.filter((_, i) => i !== idx));
      setSelectedIdx(null);
    } else {
      const next = [...value, { body_part: part, description: '' }];
      onChange(next);
      setSelectedIdx(next.length - 1);
    }
  };

  const updateInjuryNote = (idx, description) => {
    onChange(value.map((v, i) => (i === idx ? { ...v, description } : v)));
  };

  const removeInjury = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
    if (selectedIdx === idx) setSelectedIdx(null);
  };

  return (
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="flex flex-col items-center shrink-0">
        {!readOnly && (
          <div className="flex gap-1 mb-2">
            <Button
              type="button"
              size="sm"
              variant={view === 'F' ? 'default' : 'outline'}
              className="rounded-xl h-7 px-3 text-xs"
              onClick={() => setView('F')}
              data-testid="body-diagram-front-btn"
            >
              Front
            </Button>
            <Button
              type="button"
              size="sm"
              variant={view === 'B' ? 'default' : 'outline'}
              className="rounded-xl h-7 px-3 text-xs"
              onClick={() => setView('B')}
              data-testid="body-diagram-back-btn"
            >
              Back
            </Button>
          </div>
        )}
        {readOnly && (
          <div className="flex gap-1 mb-2">
            <button
              type="button"
              className={`h-7 px-3 text-xs rounded-xl border ${view === 'F' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'}`}
              onClick={() => setView('F')}
            >Front</button>
            <button
              type="button"
              className={`h-7 px-3 text-xs rounded-xl border ${view === 'B' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'}`}
              onClick={() => setView('B')}
            >Back</button>
          </div>
        )}

        <svg viewBox="0 0 100 260" className="w-40 h-[320px]" data-testid={`body-diagram-svg-${view.toLowerCase()}`}>
          <path d={SILHOUETTE_PATH} fill="#f1f5f9" stroke="#94a3b8" strokeWidth="0.8" />

          {visibleRegions.map((r) => {
            const injured = hasInjury(r.part);
            return (
              <g key={r.part} className={readOnly ? '' : 'cursor-pointer'} onClick={() => toggleRegion(r.part)}>
                <circle
                  cx={r.cx}
                  cy={r.cy}
                  r={injured ? 4 : 2.4}
                  fill={injured ? '#ef4444' : '#cbd5e1'}
                  stroke={injured ? '#991b1b' : '#64748b'}
                  strokeWidth={injured ? 0.7 : 0.4}
                  opacity={injured ? 1 : 0.55}
                >
                  <title>{r.part}{injured ? ' (marked)' : ''}</title>
                </circle>
              </g>
            );
          })}
        </svg>
        <p className="text-[10px] text-muted-foreground mt-1">
          {readOnly ? 'Marked regions' : 'Click a dot to mark / unmark'}
        </p>
      </div>

      <div className="flex-1 min-w-0 space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Marked Injuries ({value.length})
        </p>
        {value.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            {readOnly ? 'No injuries recorded.' : 'No injuries marked yet. Click a body region on the diagram.'}
          </p>
        ) : (
          <div className="space-y-2">
            {value.map((inj, idx) => (
              <div key={idx} className="rounded-lg border border-border p-2 bg-background">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">{inj.body_part}</p>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removeInjury(idx)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`remove-injury-${idx}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {readOnly ? (
                  inj.description ? (
                    <p className="text-xs text-muted-foreground mt-0.5">{inj.description}</p>
                  ) : null
                ) : (
                  <Input
                    value={inj.description || ''}
                    onChange={(e) => updateInjuryNote(idx, e.target.value)}
                    placeholder="Describe injury (bruise, scrape, laceration, redness...)"
                    className="rounded-lg h-8 text-xs mt-1"
                    data-testid={`injury-note-${idx}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BodyDiagram;
